import {useEffect, useMemo} from 'react';

import {safeLocalStorage} from '../../../../../util/localStorage';
import * as QueryTS from '../../../../../util/queryts';
import {Sort} from '../../../../../util/queryTypes';
import {SemanticLegendSettings, YAxisValues} from '../../types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../SemanticLegendDefaults';
import {useConfigValuesAggregation} from './useConfigValuesAggregation';
import {useMetricStatisticsAggregation} from './useMetricStatisticsAggregation';
import {useMetricStatisticsForRun} from './useMetricStatisticsForRun';
import {useSemanticLegendSettings} from './useSemanticLegendSettings';

export const useSemanticLegendSettingsQueryProps = ({
  createSemanticLegendSettings,
  entityName,
  filters,
  order,
  projectName,
  updateSemanticLegendSettings,
  viewID,
  viewName,
  configKeys,
}: {
  createSemanticLegendSettings: (
    settings: Partial<SemanticLegendSettings>
  ) => void;
  entityName: string;
  filters: string;
  order: Sort;
  projectName: string;
  updateSemanticLegendSettings: (
    settings: Partial<SemanticLegendSettings>
  ) => void;
  viewID: string;
  viewName: string;
  configKeys?: string[];
}) => {
  const {
    constructedSemanticLegendData,
    currentKey,
    semanticLegendSettingsError,
    semanticLegendSettingsLoading,
    shouldSkipMetricStatistics,
    shouldSkipConfigValues,
  } = useSemanticLegendSettings(viewID, configKeys);

  const {
    metricStatisticsAggregation,
    metricStatisticsError,
    metricStatisticsLoading,
  } = useMetricStatisticsAggregation({
    currentKey,
    entityName,
    projectName,
    shouldSkipMetricStatistics,
  });

  const {
    metricStatisticsForRun,
    metricStatisticsForRunError,
    metricStatisticsForRunLoading,
  } = useMetricStatisticsForRun({
    currentKey,
    entityName,
    filters,
    order: QueryTS.sortToOrderString(order),
    projectName,
    shouldSkip: shouldSkipMetricStatistics,
  });

  const {
    configValuesForRun,
    configValuesNumberAggregation,
    configValuesError,
    configValuesLoading,
  } = useConfigValuesAggregation({
    currentKey,
    entityName,
    filters,
    order,
    projectName,
    shouldSkip: shouldSkipConfigValues,
  });

  useEffect(
    () => {
      /**
       * Only when the metric statistics aggregation has changed, we need to update the semantic legend settings.
       */
      if (viewID) {
        updateSemanticLegendSettings({
          ...constructedSemanticLegendData,
          metricStatistics: {
            ...metricStatisticsAggregation,
            [YAxisValues.Config_Number]: configValuesNumberAggregation,
          },
          shouldRecalculateBuckets: true,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metricStatisticsAggregation, configValuesNumberAggregation]
  );

  const combinedError = useMemo(
    () =>
      semanticLegendSettingsError ||
      metricStatisticsError ||
      metricStatisticsForRunError ||
      configValuesError,
    [
      semanticLegendSettingsError,
      metricStatisticsError,
      metricStatisticsForRunError,
      configValuesError,
    ]
  );

  const combinedLoading = useMemo(
    () =>
      semanticLegendSettingsLoading ||
      metricStatisticsLoading ||
      metricStatisticsForRunLoading ||
      configValuesLoading,
    [
      semanticLegendSettingsLoading,
      metricStatisticsLoading,
      metricStatisticsForRunLoading,
      configValuesLoading,
    ]
  );

  // Retrieve the semantic legend settings
  const {
    data: semanticLegendSettingsData,
    specPreviouslyCreated,
  }: {data: SemanticLegendSettings; specPreviouslyCreated: boolean} =
    useMemo(() => {
      const base = {
        error: combinedError,
        loading: combinedLoading,
      };

      // If a valid semantic legend settings object exists, use it
      // we can use `currentMetric` to check this since we only have it when it destructures properly off a well-formed response
      if (currentKey) {
        return {
          ...base,
          data: {
            metricStatistics: {
              ...metricStatisticsAggregation,
              [YAxisValues.Config_Number]: configValuesNumberAggregation,
            },
            metricStatisticsForRun,
            configValuesForRun,
            ...constructedSemanticLegendData,
            viewID: viewID ?? '',
          },
          specPreviouslyCreated: true,
        };
      }
      // if no semantic legend settings object exists, check local storage for a saved semantic legend settings object
      const semanticLegendFromStorage = safeLocalStorage.getItem(
        `semanticLegendSettings-${viewName}`
      );
      if (semanticLegendFromStorage) {
        const parsedSemanticLegendFromStorage = {
          ...JSON.parse(semanticLegendFromStorage),
          viewID: viewID ?? '',
        };
        // if the view id is not empty and we're not waiting for any new data to load,
        // create the semantic legend settings from the stored object in local storage for the current view
        if (viewID && !semanticLegendSettingsLoading) {
          createSemanticLegendSettings(parsedSemanticLegendFromStorage);
          // remove the saved semantic legend settings from local storage because it's now stored in the backend
          safeLocalStorage.removeItem(`semanticLegendSettings-${viewName}`);
        }
        return {
          ...base,
          data: parsedSemanticLegendFromStorage,
          specPreviouslyCreated: true,
        };
      }

      // default to the default semantic legend settings
      return {
        ...base,
        data: {
          ...DEFAULT_SEMANTIC_LEGEND_SETTINGS,
          viewID: viewID ?? '',
        },
        specPreviouslyCreated: false,
      };
    }, [
      combinedError,
      combinedLoading,
      configValuesNumberAggregation,
      configValuesForRun,
      constructedSemanticLegendData,
      createSemanticLegendSettings,
      currentKey,
      metricStatisticsAggregation,
      metricStatisticsForRun,
      semanticLegendSettingsLoading,
      viewID,
      viewName,
    ]);

  return useMemo(
    () => ({
      semanticLegendSettingsData,
      semanticLegendSettingsLoading,
      semanticLegendSettingsError,
      specPreviouslyCreated,
    }),
    [
      semanticLegendSettingsData,
      semanticLegendSettingsLoading,
      semanticLegendSettingsError,
      specPreviouslyCreated,
    ]
  );
};
